import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const lastUpdated = "January 25, 2025";

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-3">
          <button 
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <ArrowLeft size={20} className="text-gray-600" />
          </button>
          <h1 className="text-xl font-bold text-[#1e3a5f]">Privacy Policy</h1>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="bg-white rounded-xl shadow-sm p-6 md:p-8 space-y-6">
          <div className="text-sm text-gray-500">Last updated: {lastUpdated}</div>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-[#1e3a5f]">Introduction</h2>
            <p className="text-gray-700 leading-relaxed">
              Honey™ Cooking Companion ("we," "our," or "us") is committed to protecting your privacy. 
              This Privacy Policy explains how we collect, use, disclose, and safeguard your information 
              when you use our mobile application and website (collectively, the "Service").
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-[#1e3a5f]">Information We Collect</h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <div>
                <h3 className="font-medium text-gray-900">Personal Information</h3>
                <p>When you create an account using Google Sign-In, we collect:</p>
                <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                  <li>Your name</li>
                  <li>Email address</li>
                  <li>Profile picture (if provided by Google)</li>
                </ul>
              </div>
              <div>
                <h3 className="font-medium text-gray-900">User Content</h3>
                <p>We collect content you create, upload, or share, including:</p>
                <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                  <li>Recipes you create, import, or save</li>
                  <li>Photos you upload</li>
                  <li>Notes and modifications you make to recipes</li>
                  <li>Posts and comments in Hives (community groups)</li>
                  <li>Food preferences and dietary restrictions you set</li>
                </ul>
              </div>
              <div>
                <h3 className="font-medium text-gray-900">Usage Information</h3>
                <p>We automatically collect certain information about how you use the Service, including:</p>
                <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                  <li>Features you use and actions you take</li>
                  <li>Device information (device type, operating system)</li>
                  <li>Log data (access times, pages viewed)</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-[#1e3a5f]">How We Use Your Information</h2>
            <p className="text-gray-700 leading-relaxed">We use the information we collect to:</p>
            <ul className="list-disc list-inside ml-4 text-gray-700 space-y-1">
              <li>Provide, maintain, and improve our Service</li>
              <li>Personalize your experience (e.g., AI-generated recipes based on your preferences)</li>
              <li>Enable social features like Hives and Buzz</li>
              <li>Generate shopping lists and meal plans</li>
              <li>Send you updates and notifications (with your consent)</li>
              <li>Analyze usage patterns to improve the Service</li>
              <li>Protect against fraud and abuse</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-[#1e3a5f]">AI-Powered Features</h2>
            <p className="text-gray-700 leading-relaxed">
              Our Service uses artificial intelligence to provide features like recipe generation, 
              cooking advice, and recipe scaling. When you interact with AI features:
            </p>
            <ul className="list-disc list-inside ml-4 text-gray-700 space-y-1">
              <li>Your inputs (questions, recipe requests) are processed by third-party AI providers</li>
              <li>We do not use your personal recipes to train AI models</li>
              <li>AI-generated content is provided for informational purposes</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-[#1e3a5f]">Sharing Your Information</h2>
            <p className="text-gray-700 leading-relaxed">We may share your information in the following situations:</p>
            <ul className="list-disc list-inside ml-4 text-gray-700 space-y-1">
              <li><strong>With your consent:</strong> When you choose to share recipes or posts publicly or with Hives</li>
              <li><strong>Service providers:</strong> Third parties that help us operate our Service (hosting, AI providers)</li>
              <li><strong>Legal requirements:</strong> When required by law or to protect our rights</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-2">
              We do <strong>not</strong> sell your personal information to third parties.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-[#1e3a5f]">Data Security</h2>
            <p className="text-gray-700 leading-relaxed">
              We implement appropriate technical and organizational measures to protect your personal 
              information. However, no method of transmission over the Internet is 100% secure, and 
              we cannot guarantee absolute security.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-[#1e3a5f]">Your Rights and Choices</h2>
            <p className="text-gray-700 leading-relaxed">You have the right to:</p>
            <ul className="list-disc list-inside ml-4 text-gray-700 space-y-1">
              <li>Access and download your data</li>
              <li>Update or correct your information</li>
              <li>Delete your account and associated data</li>
              <li>Control your privacy settings for Hives and shared content</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-2">
              To exercise these rights, please contact us at the email below.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-[#1e3a5f]">Children's Privacy</h2>
            <p className="text-gray-700 leading-relaxed">
              Our Service is not intended for children under 13 years of age. We do not knowingly 
              collect personal information from children under 13.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-[#1e3a5f]">Changes to This Policy</h2>
            <p className="text-gray-700 leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of any changes 
              by posting the new policy on this page and updating the "Last updated" date.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-[#1e3a5f]">Contact Us</h2>
            <p className="text-gray-700 leading-relaxed">
              If you have questions about this Privacy Policy, please contact us at:
            </p>
            <p className="text-gray-700">
              <strong>Email:</strong> <a href="mailto:honeycookingcompanion@gmail.com" className="text-amber-600 hover:underline">honeycookingcompanion@gmail.com</a>
            </p>
          </section>

          <div className="pt-6 border-t text-center text-sm text-gray-500">
            Honey™ Cooking Companion
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
